import { Button }                                 from 'antd';
import { AppFormInjectedProps }                   from 'components/App/AppForm';
import { AddStandSuccessParams }                  from 'forms/AddStandMultiStepsForm/types';
import { AddStandMultiStepsFormProps }            from 'forms/AddStandMultiStepsForm/types';
import { ExhibitorData }                          from 'forms/AddStandMultiStepsForm/types';
import { useCallback }                            from 'react';
import { useEffect }                              from 'react';
import { useState }                               from 'react';
import { Steps }                                  from 'antd';
import { message }                                from 'antd';
import { Form }                                   from 'antd';
import FormattedMessageWrapper                    from 'components/App/FormattedMessageWrapper';
import { appStore }                               from 'stores/AppStore';
import handleFormError                            from 'utils/handleFormError';
import { formatMessageIntl }                      from 'utils/messageIntl';
import NewAddStandStep1                           from './StepExhibitor';
import StepStand                                  from './StepStand';
import { ROLE_STANDIST }                          from 'constants/RoleReferences';
import UserStandRoleModel                         from 'models/UserStandRoleModel';
import { ROLE_STANDIST_EXHIBITOR }                from 'constants/StandistRoleReference';
import { ASK_PLANS, ASK_PLANS_WITH_ACCESS_LOGIN } from 'constants/NotificationReferences';
import RoleModel                                  from 'models/RoleModel';
import StandModel                                 from 'models/StandModel';
import ShowModel                                  from 'models/ShowModel';

const initialNewExhibitor: ExhibitorData = {
  company: '',
  email: '',
  firstName: '',
  id: undefined,
  languagePreference: undefined,
  lastName: '',
  phone: '',
  plainPassword: undefined,
  plainPasswordConfirmation: undefined,
  role: undefined,
};

const AddStandMultiStepsForm = ({
  closeModal,
  exhibitor,
  onSuccess,
  setModalFooter,
  showId,
}: AddStandMultiStepsFormProps & AppFormInjectedProps) => {
  const [form] = Form.useForm();

  const [current, setCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [newExhibitor, setNewExhibitor] = useState<ExhibitorData>(initialNewExhibitor);

  const clearExhibitorData = () => {
    form.setFieldsValue(initialNewExhibitor);
    setNewExhibitor(initialNewExhibitor);
  };

  const onFinish = async (values: any) => {
    const { roleCollection } = appStore;

    if (setIsLoading) {
      setIsLoading(true);
    }

    const roleStandist = roleCollection.findBy('reference', ROLE_STANDIST) as RoleModel;

    if (!roleStandist) {
      message.error(formatMessageIntl("Une erreur s'est produite."));
      return;
    }

    let exhibitorIsNew = true;

    if (newExhibitor.id) {
      exhibitor.setId(newExhibitor.id);
      exhibitorIsNew = false;
    }

    try {
      await exhibitor.patch({
        company: values.company,
        email: values.email,
        firstName: values.firstName,
        id: newExhibitor.id,
        languagePreference: values.languagePreference,
        lastName: values.lastName,
        phone: values.phone,
        plainPassword: values.plainPassword ? values.plainPassword : undefined,
        plainPasswordConfirmation: values.plainPasswordConfirmation
          ? values.plainPasswordConfirmation
          : undefined,
        role: !exhibitor.role ? roleStandist.iri : exhibitor.roleIri,
      });
    } catch (error) {
      handleFormError(error, form);
      setCurrent(0);
      return;
    }

    const stand = new StandModel();

    try {
      await stand.patch({
        aisle: values.aisle,
        hall: values.hall,
        name: values.name,
        number: values.number,
        show: new ShowModel().path + showId,
        standType: values.standType,
        surface: values.surface,
      });
    } catch (error) {
      handleFormError(error, form);
      setCurrent(1);
      return;
    }

    const userStandRole = new UserStandRoleModel();

    try {
      await userStandRole.patch({
        stand: stand.iri,
        standistRole: ROLE_STANDIST_EXHIBITOR,
        user: exhibitor.iri,
      });
    } catch {
      message.error(formatMessageIntl("Une erreur s'est produite."));
      return;
    }

    if (onSuccess) {
      const successData: AddStandSuccessParams = {
        exhibitor,
        openNotificationModal: values.openNotificationModal,
        stand,
        tag: exhibitorIsNew ? ASK_PLANS_WITH_ACCESS_LOGIN.tag : ASK_PLANS.tag,
      };
      onSuccess(successData);
    }

    if (setIsLoading) {
      setIsLoading(false);
    }
  };

  const handleNext = useCallback(async () => {
    try {
      if (current === 0) {
        await form.validateFields([
          'company',
          'email',
          'firstName',
          'id',
          'lastName',
          'phone',
          'plainPassword',
          'plainPasswordConfirmation',
          'role',
        ]);
      }
      setCurrent(current + 1);
    } catch {
      // errors already handled by ant
    }
  }, [current, form]);

  useEffect(() => {
    setModalFooter([
      <Button
        key="cancel"
        onClick={closeModal}
      >
        Annuler
      </Button>,
      current > 0 && (
        <Button
          key="prev"
          onClick={() => setCurrent(current - 1)}
        >
          Précédent
        </Button>
      ),
      current === 0 && (
        <Button
          key="next"
          type="primary"
          onClick={handleNext}
        >
          Suivant
        </Button>
      ),
      current === 1 && (
        <Button
          key="submit"
          loading={isLoading}
          type="primary"
          onClick={() => form.submit()}
        >
          Confirmer
        </Button>
      ),
    ].filter(Boolean));
  }, [closeModal, current, form, handleNext, isLoading, setModalFooter]);

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
    >
      <div>
        <Steps
          current={current}
          items={[
            { title: <FormattedMessageWrapper id="Informations de l'exposant" />, },
            { title: <FormattedMessageWrapper id="Informations du stand" />, },
          ]}
        />

        <br />

        <div className="steps-content">
          <div style={{ display: current === 0 ? 'block' : 'none' }}>
            <NewAddStandStep1
              clearExhibitorData={clearExhibitorData}
              exhibitor={newExhibitor}
              setExhibitor={exhibitor => {
                form.setFieldsValue(exhibitor);
                setNewExhibitor(exhibitor);
              }}
            />
          </div>

          <div style={{ display: current === 1 ? 'block' : 'none' }}>
            <StepStand />
          </div>
        </div>
      </div>
    </Form>
  );
}

export default AddStandMultiStepsForm;
